import {useId, useMemo, useState} from 'react'
import {BlockContainer} from './components/BlockContainer'
import {CodeBlock} from './components/CodeBlock'
import {LinkArrow} from './components/LinkArrow'
import {SelectorCard} from './components/SelectorCard'

export type Route = {
  routeLabel: string
  routeDescription: string
  isAvailable: boolean
  isRecommended: boolean
  promptText: string
  failureNote: string
}

export type HandoffBlockProps = {
  heading: string
  selfIdPrompt: string
  trustSummary: string
  trustLinkAnchor: string
  routes: Route[]
  pageUid: string
}

export const HandoffBlock = ({
  heading,
  selfIdPrompt,
  trustSummary,
  trustLinkAnchor,
  routes,
  pageUid,
}: HandoffBlockProps) => {
  const groupName = useId()
  const firstAvailableIndex = routes.findIndex((route) => route.isAvailable)
  const [selectedIndex, setSelectedIndex] = useState(firstAvailableIndex === -1 ? 0 : firstAvailableIndex)
  const selectedRoute = routes[selectedIndex]

  const selectedPromptLines = useMemo(
    () => (selectedRoute?.promptText ? selectedRoute.promptText.split('\n') : []),
    [selectedRoute],
  )

  return (
    <section
      data-slice-type="discovery_block"
      data-slice-variation="handoffBlock"
      data-nav-dark="true"
      className="grid grid-cols-main bg-green-base bg-discovery-glow bg-fixed px-5 py-12 md:px-10 md:py-16"
    >
      <div className="col-[inner] w-full max-w-[46.25rem] justify-self-center">
        <BlockContainer headline={heading} subhead={selfIdPrompt}>
          <div className="flex flex-col gap-5">
            {routes.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {routes.map((route, routeIndex) => (
                  <SelectorCard
                    key={routeIndex}
                    name={groupName}
                    title={route.routeLabel}
                    description={route.routeDescription}
                    isSelected={routeIndex === selectedIndex}
                    isAvailable={route.isAvailable}
                    isRecommended={route.isRecommended}
                    onSelect={() => setSelectedIndex(routeIndex)}
                    trackingData={{
                      mktgId: 'discovery-block-route',
                      location: 'handoff-block',
                      dataType: 'route-selector',
                      pageName: pageUid,
                      label: route.routeLabel,
                    }}
                  />
                ))}
              </div>
            )}
            {selectedRoute?.isAvailable && selectedPromptLines.length > 0 && (
              <CodeBlock key={selectedIndex} lines={selectedPromptLines} />
            )}
            {selectedRoute?.isAvailable && selectedRoute.failureNote && (
              <p className="text-14 text-green-mist-dim">{selectedRoute.failureNote}</p>
            )}
            {trustSummary && (
              <div className="border-t border-moss-white/[8%] pt-5">
                <LinkArrow
                  href={`#${trustLinkAnchor}`}
                  trackingData={{
                    mktgId: 'discovery-block-trust-summary',
                    location: 'handoff-block',
                    dataType: 'trust-summary-link',
                    pageName: pageUid,
                  }}
                >
                  {trustSummary}
                </LinkArrow>
              </div>
            )}
          </div>
        </BlockContainer>
      </div>
    </section>
  )
}
